/**
 * @file    dataSourceResolver.ts
 * @scope   SHARED
 * @summary Picks the mock data source that feeds the live preview, and derives the Guardrails
 *          data source type ("survey" | "chat" | "generic") for the widget being edited.
 * @consumes DiyMockDataSource catalogue, RulesName, chartsWithTextControllers
 * @produces resolvePreviewDataSource, resolveSourceType
 * @sideEffects None. `resolvePreviewDataSource` returns the rule name instead of setting state,
 *              so the caller decides when to commit it.
 */
import { RulesName } from 'constants/rulesName';
import { DiyMockDataSource } from 'constants/diyMockDataSources';
import { TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';
import { chartsWithTextControllers } from '../constants/chartCapabilities';

/** Result of a preview data source lookup. */
export type PreviewDataSourceResult = {
  /** Mock widget used to render the preview, `undefined` when no mock matches. */
  dataSource: any | undefined;
  /**
   * Rule name backing a child chart. `undefined` for non-child charts.
   * WHY: the drawer keeps this in state to decide whether the calendar filter is shown.
   */
  ruleName?: string;
};

/**
 * Resolves the mock data source used by the drawer preview.
 *
 * Resolution order:
 * 1. Non-child charts → match the mock by alias.
 * 2. Child charts → match by business rule name.
 * 3. `OneQuestionOneCalculation` → the mock also depends on the selected trend, so the trend
 *    display name is appended to the rule name.
 *
 * @param alias              Component alias of the chart to preview.
 * @param selectedDiyChildChart Chart option selected in the dropdown (may be a child chart).
 * @param selectedChartTrend Trend currently selected in the drawer.
 * @param configTrend        Trend stored in `customWidgetConfig.Trend` (used on first render).
 */
export const resolvePreviewDataSource = (
  alias: string,
  selectedDiyChildChart: any,
  selectedChartTrend?: TrendOptions,
  configTrend?: any[]
): PreviewDataSourceResult => {
  if (!selectedDiyChildChart?.isChild) {
    return { dataSource: DiyMockDataSource.find((widget) => widget.alias === alias) };
  }

  const [ruleName] = selectedDiyChildChart.rules || [];
  const resolvedRuleName: string = ruleName ? ruleName : alias;

  if (resolvedRuleName === RulesName.OneQuestionOneCalculation) {
    // WHY: this rule renders a different mock per trend period, so the trend label is part of
    // the mock alias. The config trend is only used before the user picks one explicitly.
    let trendValue =
      configTrend !== undefined && configTrend.length > 0
        ? configTrend[0].displayName
        : selectedChartTrend?.trend.displayName;
    if (selectedChartTrend?.trend?.displayName !== undefined)
      trendValue = selectedChartTrend.trend.displayName;

    const trendAlias = resolvedRuleName + trendValue;
    return {
      dataSource: DiyMockDataSource.find((widget) => widget.alias === trendAlias),
      ruleName: resolvedRuleName,
    };
  }

  return {
    dataSource:
      DiyMockDataSource.find((widget) => widget.alias === resolvedRuleName) ||
      DiyMockDataSource.find((widget) => widget.alias === alias),
    ruleName: resolvedRuleName,
  };
};

/**
 * Constructs a data source for the preview by merging the mock data source with payload overrides.
 *
 * The payload is enriched with the current chart type and partition (if selected).
 * The resulting object is mutated (alias key removed) and returned for setState.
 *
 * WHY THIS FUNCTION: Eliminates duplicate construction logic that appeared in two places in
 * DiyDrawer effects ([currentAlias] and [selectedQuestions]). Both effects need the same
 * transformation: pull the mock, add metadata, and remove the alias key.
 *
 * @param dataSource        Mock widget from `resolvePreviewDataSource` result.
 * @param currentAlias      Current chart selection metadata (chartType, component, alias).
 * @param selectedPartition Currently selected partition (for enterprise grids).
 * @returns New data source object with payload overrides; alias key removed.
 */
export const buildPreviewDataSource = (
  dataSource: any,
  currentAlias: any,
  selectedPartition?: any
): any => {
  const newDataSource: any = {
    ...dataSource,
    payload: {
      ...dataSource?.payload,
      ControllerName: currentAlias.chartType,
      partitionGuid: selectedPartition ? selectedPartition.value : undefined,
    },
  };

  delete newDataSource.alias;
  return newDataSource;
};

/**
 * Derives the Guardrails `DataSource` value for a chart.
 *
 * @param alias         Widget alias being edited.
 * @param dashboardView Current dashboard view (`'survey'` or anything else → chat).
 * @returns `'generic'` for text widgets (they have no backing data), otherwise the view's source.
 */
export const resolveSourceType = (alias: string, dashboardView: string): string => {
  if (chartsWithTextControllers.includes(alias)) return 'generic';
  return dashboardView === 'survey' ? 'survey' : 'chat';
};
